import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  deleteTeamMember,
  getTournamentCategoryDetail,
  updateTeamMemberStatus,
  getTeamMatches,
  updateTeam,
  reassignCaptain,
} from "../../api/tournaments";
import { ITeamDetail, ITournamentCategoryDetail } from "../../models/ITournament";
import { ITeamMatch, IMatchDetailDTO } from "../../models/IMatch";
import {
  BrandTelegram,
  CheckOctagon,
  DangerOctagon,
  Envelope,
  LetterCSquare,
  Scan,
  Telephone,
  Trash,
  Users,
} from "@mynaui/icons-react";
import PlayerStatusDialog from "../../components/PlayerStatusDialog";
import AddTeamMemberDialog from "../../components/AddTeamMemberDialog";
import { IPlayerDetail } from "../../models/IPlayer";
import { Eye, Pencil, Trophy, UserPlus, X, Check, Shield } from "lucide-react";
import moment from "moment";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useAppDispatch, useAppSelector } from "../../store/store";
import MatchCard from "../../components/MatchCard";
import { generateBulkVCard } from "../../utils/vcard";
import { isSuperAdmin, isRegistrationManager } from "../../utils/authorization";
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  IconButton,
  Input,
  Loader,
  Menu,
  PageHeader,
  Spinner,
  TableWrap,
  Tabs,
  Tag,
  Toolbar,
} from "../../ui";
import type { TagTone } from "../../ui";
import "./styles.scss";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

// A member added by name only (no player account) has no playerId, or the empty GUID.
const isRegisteredPlayer = (member: IPlayerDetail) => !!member.playerId && member.playerId !== EMPTY_GUID;

// The team pill: the words of the status picked the old pill's colour (pending / confirmed / canceled).
const teamStatusTone = (status?: string): TagTone => {
  const words = (status ?? "").toLowerCase().split(/\s+/);
  if (words.includes("pending")) return "warn";
  if (words.includes("confirmed")) return "ok";
  if (words.includes("canceled") || words.includes("cancelled") || words.includes("rejected")) return "danger";
  return "neutral";
};

const registrationTone = (status: string): TagTone =>
  status.includes("Pending") ? "warn" : status === "Confirmed" ? "ok" : "danger";

const paymentTone = (status: string): TagTone =>
  status.includes("Pending") || status.includes("Cash") ? "warn" : status === "Paid" ? "ok" : "danger";

const TeamDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const currentUser = useAppSelector((state) => state.auth.currentUser);

  const { tournamentCategoryId, teamId } = useParams();

  const [loading, setLoading] = useState(true);
  const [teamDetail, setTeamDetail] = useState<ITeamDetail>();
  const [activePlayer, setActivePlayer] = useState<IPlayerDetail>();
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<"members" | "matches">(() => {
    const tab = searchParams.get("tab") as "members" | "matches" | null;
    return tab || "members";
  });
  const [matches, setMatches] = useState<ITeamMatch[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [maxPlayers, setMaxPlayers] = useState(0);
  const [assigningCaptainId, setAssigningCaptainId] = useState<string | null>(null);
  const [isEditingTeamName, setIsEditingTeamName] = useState(false);
  const [editedTeamName, setEditedTeamName] = useState("");
  const [isUpdatingTeamName, setIsUpdatingTeamName] = useState(false);

  const hasOpenedFromMemberId = useRef(false);

  const getTournamentData = async () => {
    try {
      const { data } = await getTournamentCategoryDetail(tournamentCategoryId!);
      const categoryData = data as ITournamentCategoryDetail;
      const teamDetail = categoryData.teams?.find((x) => x.id === teamId);
      setTeamDetail(teamDetail);
      setMaxPlayers(categoryData.maxPlayers || 0);
      document.title = `${data?.categoryName} - ${teamDetail?.teamName}`;

      const queryParams = new URLSearchParams(location.search);
      const memberId = queryParams.get("memberId");

      if (memberId && !hasOpenedFromMemberId.current) {
        const found = teamDetail?.teamMembers.find((x) => x.id === memberId);
        if (found) {
          setActivePlayer(found);
          hasOpenedFromMemberId.current = true;
        }
      }
    } catch (err: any) {
    } finally {
      setLoading(false);
    }
  };

  const getTeamMatchesData = async () => {
    if (!teamId) return;

    setMatchesLoading(true);
    try {
      const { data } = await getTeamMatches(teamId);
      setMatches(data);
    } catch (err: any) {
      console.error("Error fetching team matches:", err);
    } finally {
      setMatchesLoading(false);
    }
  };

  const handleTabChange = (tab: "members" | "matches") => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleBack = () => {
    const goTo = searchParams.get("goTo");
    const playerId = searchParams.get("playerId");

    if (goTo === "player" && playerId) {
      navigate(`/dashboard/players/${playerId}`);
    } else {
      navigate(-1);
    }
  };

  const toggleCheckAll = () => {
    if (teamDetail?.teamMembers) {
      if (checkedItems.length === Number(teamDetail?.teamMembers.length)) setCheckedItems([]);
      else if (checkedItems.length < Number(teamDetail?.teamMembers.length))
        setCheckedItems(teamDetail?.teamMembers.map((x) => x.id));
    }
  };

  const toggleCheck = (id: string) => {
    setCheckedItems((prev) => {
      let updatedItems = [...prev];
      const itemId = updatedItems.findIndex((x) => x === id);

      if (itemId !== -1) {
        updatedItems = updatedItems.filter((x) => x !== id);
      } else updatedItems.push(id);

      return updatedItems;
    });
  };

  const handleCancel = () => {
    setCheckedItems([]);
  };

  const openContacts = () => {
    const checkedMembers = teamDetail?.teamMembers?.filter((x) => checkedItems.includes(x.id));

    if (!checkedMembers || checkedMembers.length === 0) return;

    const contacts = checkedMembers.map((member) => ({
      name: `${member.firstName} ${member.lastName || ""}`.trim(),
      phone: member.phoneNumber || "",
      email: member.email || undefined,
      tournament: teamDetail?.tournamentName || "",
      category: teamDetail?.categoryName || "",
    }));

    const vcard = generateBulkVCard(contacts);
    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);

    // Open in a new tab (mobile will usually trigger "Add to Contacts")
    window.open(url, "_blank");
  };

  const addMemberToContacts = (member: IPlayerDetail) => {
    const contact = {
      name: `${member.firstName} ${member.lastName || ""}`.trim(),
      phone: member.phoneNumber || "",
      email: member.email || undefined,
      tournament: teamDetail?.tournamentName || "",
      category: teamDetail?.categoryName || "",
    };

    const vcard = generateBulkVCard([contact]);
    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);

    // Open in a new tab (mobile will usually trigger "Add to Contacts")
    window.open(url, "_blank");
  };

  const handleBulkConfirm = async () => {
    setLoading(true);
    try {
      const checkedPlayers = teamDetail?.teamMembers?.filter((x) => checkedItems.includes(x.id));

      if (checkedPlayers && checkedPlayers.length > 0) {
        Promise.allSettled(
          checkedPlayers.map((player, id) =>
            updateTeamMemberStatus(teamDetail?.id!, player.id, { status: "Confirmed", paymentStatus: "Paid" })
          )
        );
      }
      getTournamentData();
    } catch (error) {
      console.error("Error confirming player selection");
    } finally {
      setCheckedItems([]);
    }
  };

  const canEditTeam = () => {
    if (!currentUser || !teamDetail) return false;
    // Check if user is team creator, superadmin, or registration manager
    return teamDetail.createdBy === currentUser.id || isSuperAdmin(currentUser) || isRegistrationManager(currentUser);
  };

  const handleEditTeamName = () => {
    if (teamDetail) {
      setEditedTeamName(teamDetail.teamName);
      setIsEditingTeamName(true);
    }
  };

  const handleCancelEditTeamName = () => {
    setIsEditingTeamName(false);
    setEditedTeamName("");
  };

  const handleSaveTeamName = async () => {
    if (!teamId || !editedTeamName.trim()) {
      dispatch(setGeneralAlertMessage({ message: "Team name cannot be empty", type: "error" }));
      return;
    }

    if (editedTeamName.trim() === teamDetail?.teamName) {
      setIsEditingTeamName(false);
      return;
    }

    setIsUpdatingTeamName(true);
    try {
      await updateTeam(teamId, { name: editedTeamName.trim() });
      dispatch(setGeneralAlertMessage({ message: "Team name updated successfully", type: "success" }));
      setIsEditingTeamName(false);
      getTournamentData();
    } catch (error: any) {
      console.error("Error updating team name:", error);
      dispatch(
        setGeneralAlertMessage({
          message: error.message || "Failed to update team name",
          type: "error",
        })
      );
    } finally {
      setIsUpdatingTeamName(false);
    }
  };

  const handleDeleteTeam = async () => {};

  const handleDeleteTeamMember = async (id: string) => {
    setLoading(true);
    try {
      const res = await deleteTeamMember(id);
      if (res.data) {
        getTournamentData();
        dispatch(setGeneralAlertMessage({ message: res.message, type: "success" }));
      }
    } catch (error: any) {
      console.error("team member deletion failed", error.message);
      dispatch(setGeneralAlertMessage({ message: error.message, type: "error" }));
      setLoading(false);
    }
  };

  const handleAssignCaptain = async (teamMemberId: string) => {
    setAssigningCaptainId(teamMemberId);
    try {
      const res = await reassignCaptain(teamId!, teamMemberId);
      if (res.data?.success !== false) {
        getTournamentData();
        dispatch(setGeneralAlertMessage({ message: res.data?.message || "Captain reassigned successfully", type: "success" }));
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error.message || "Failed to reassign captain";
      dispatch(setGeneralAlertMessage({ message: msg, type: "error" }));
    } finally {
      setAssigningCaptainId(null);
    }
  };

  useEffect(() => {
    getTournamentData();
    getTeamMatchesData(); // Fetch matches data immediately to show count
  }, []);

  // Handle URL changes for tab persistence
  useEffect(() => {
    const tab = searchParams.get("tab") as "members" | "matches" | null;
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const memberId = queryParams.get("memberId");

    if (memberId) {
      const found = teamDetail?.teamMembers.find((x) => x.id === memberId);
      if (found) setActivePlayer(found);
    }
  }, [teamDetail]);

  const memberCount = teamDetail?.teamMembers.length ?? 0;

  return (
    <div className="team-detail">
      {loading ? (
        <Loader />
      ) : (
        <>
          <PageHeader
            back={{ onClick: handleBack }}
            eyebrow={teamDetail?.categoryName}
            title={
              isEditingTeamName ? (
                <span className="team-detail__name-edit">
                  <Input
                    className="team-detail__name-input"
                    block={false}
                    value={editedTeamName}
                    onChange={(e) => setEditedTeamName(e.target.value)}
                    disabled={isUpdatingTeamName}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSaveTeamName();
                      } else if (e.key === "Escape") {
                        handleCancelEditTeamName();
                      }
                    }}
                  />
                  <IconButton
                    label="Save"
                    icon={<Check size={16} />}
                    variant="tint"
                    onClick={handleSaveTeamName}
                    disabled={isUpdatingTeamName || !editedTeamName.trim()}
                  />
                  <IconButton
                    label="Cancel"
                    icon={<X size={16} />}
                    onClick={handleCancelEditTeamName}
                    disabled={isUpdatingTeamName}
                  />
                </span>
              ) : (
                <span className="team-detail__title">
                  {teamDetail?.teamName}
                  {canEditTeam() && (
                    <IconButton
                      className="team-detail__edit"
                      label="Edit team name"
                      icon={<Pencil size={16} />}
                      size="sm"
                      onClick={handleEditTeamName}
                    />
                  )}
                </span>
              )
            }
            subtitle={
              <span className="team-detail__meta">
                <Tag tone={teamStatusTone(teamDetail?.status)}>{teamDetail?.status}</Tag>
                <span>{teamDetail?.teamMembers.length} Players</span>
                <span className="team-detail__dot" aria-hidden="true" />
                <span>Registered {moment(teamDetail?.createdAt).format("DD MMMM YYYY")}</span>
                <span className="team-detail__dot" aria-hidden="true" />
                <span>Registered by {teamDetail?.captain}</span>
              </span>
            }
            // actions={<Button variant="danger" onClick={handleDeleteTeam}>Delete</Button>}
          />

          <Card padding="sm" className="team-detail__info">
            <div className="team-detail__info-row">
              <div className="team-detail__info-item">
                <LetterCSquare size={20} />
                <span>{teamDetail?.captain}</span>
              </div>
              <div className="team-detail__info-item">
                <Scan size={20} />
                <span>{teamDetail?.promoCode ? teamDetail.promoCode : "No promo code"}</span>
              </div>
            </div>
          </Card>

          <Tabs
            className="team-detail__tabs"
            ariaLabel="Team sections"
            value={activeTab}
            onChange={handleTabChange}
            options={[
              { value: "members", label: "Team Members", count: memberCount > 0 ? memberCount : undefined },
              { value: "matches", label: "Matches", count: matches.length > 0 ? matches.length : undefined },
            ]}
          />

          {activeTab === "members" && (
            <div className="team-detail__members">
              <Toolbar
                actions={
                  checkedItems.length > 0 ? (
                    <>
                      <Button variant="ghost" onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button variant="tint" onClick={openContacts}>
                        Add to Contacts ({checkedItems.length})
                      </Button>
                      <Button variant="primary" onClick={handleBulkConfirm}>
                        Confirm Selection ({checkedItems.length})
                      </Button>
                    </>
                  ) : maxPlayers === 0 || (teamDetail?.teamMembers?.length ?? 0) < maxPlayers ? (
                    <Button variant="primary" icon={<UserPlus size={16} />} onClick={() => setShowAddMemberDialog(true)}>
                      Add Team Member
                    </Button>
                  ) : null
                }
              >
                <p className="team-detail__selected">{checkedItems.length > 0 ? `${checkedItems.length} selected` : ""}</p>
              </Toolbar>
              <TableWrap className="team-detail__table">
                <table className="ui-table">
                  <thead>
                    <tr>
                      <th className="team-detail__col-check">
                        <Checkbox
                          label="Select all"
                          hideLabel
                          checked={checkedItems.length === teamDetail?.teamMembers.length}
                          indeterminate={checkedItems.length > 0 && checkedItems.length < memberCount}
                          onChange={() => toggleCheckAll()}
                        />
                      </th>
                      <th className="team-detail__col-name">Name</th>
                      <th>Email</th>
                      <th>Phone Number</th>
                      <th>Nationality</th>
                      <th className="team-detail__col-center">Consent</th>
                      <th>Registration Status</th>
                      <th>Payment Status</th>
                      <th>Chosen Fee</th>
                      <th>Admin Notes</th>
                      <th className="team-detail__col-actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {teamDetail?.teamMembers.map((member, rowIndex) => {
                      const isChecked = checkedItems.includes(member.id);
                      return (
                        <tr
                          key={rowIndex}
                          className={`is-clickable${isChecked ? " is-selected" : ""}`}
                          onClick={() => {
                            if (checkedItems.length === 0) {
                              setActivePlayer(member);
                              const params = new URLSearchParams(location.search);
                              params.set("memberId", member.id);
                              navigate({ search: params.toString() }, { replace: true });
                            } else toggleCheck(member.id);
                          }}
                        >
                          <td className="team-detail__col-check">
                            <span className="team-detail__check" onClick={(evt) => evt.stopPropagation()}>
                              <Checkbox
                                label={`Select ${member.firstName} ${member.lastName ?? ""}`}
                                hideLabel
                                checked={isChecked}
                                onChange={() => toggleCheck(member.id)}
                              />
                            </span>
                          </td>
                          <td className="team-detail__col-name">
                            <div className="team-detail__member">
                              <span className="team-detail__member-name">
                                {member.firstName} {member.lastName ?? ""}
                              </span>
                              {member.isCaptain && <LetterCSquare size={18} className="team-detail__captain" />}
                              {!isRegisteredPlayer(member) && <Tag tone="neutral">Non-registered</Tag>}
                            </div>
                          </td>
                          <td>{isRegisteredPlayer(member) ? member.email || "-" : "-"}</td>
                          <td>{isRegisteredPlayer(member) ? member.phoneNumber || "-" : "-"}</td>
                          <td>{isRegisteredPlayer(member) ? member.nationality || "-" : "-"}</td>
                          <td className="team-detail__col-center">
                            {member.consent ? (
                              <CheckOctagon size={20} className="team-detail__consent team-detail__consent--yes" />
                            ) : (
                              <DangerOctagon size={20} className="team-detail__consent team-detail__consent--no" />
                            )}
                          </td>
                          <td>
                            <Tag tone={registrationTone(member.registrationStatus)}>{member.registrationStatus}</Tag>
                          </td>
                          <td>
                            <Tag tone={paymentTone(member.paymentStatus)}>{member.paymentStatus}</Tag>
                          </td>
                          <td>
                            {member.fee ? `${member.fee.description} - ${member.fee.amount} ${member.fee.currency}` : "-"}
                          </td>
                          <td>{member.notes ?? "-"}</td>
                          <td className="ui-table__actions team-detail__col-actions" onClick={(e) => e.stopPropagation()}>
                            <Menu
                              items={[
                                {
                                  // The member's name heads the menu, as before (not an action)
                                  label: (
                                    <span className="team-detail__menu-name">
                                      {member.firstName} {member.lastName ?? ""}
                                    </span>
                                  ),
                                  onClick: () => {},
                                  disabled: true,
                                },
                                {
                                  label: "Edit",
                                  icon: <Pencil size={16} />,
                                  onClick: () => {
                                    setActivePlayer(member);
                                  },
                                },
                                {
                                  label: assigningCaptainId === member.teamMemberId ? "Assigning..." : "Assign Captain",
                                  icon:
                                    assigningCaptainId === member.teamMemberId ? <Spinner size="sm" /> : <Shield size={16} />,
                                  visible:
                                    !member.isCaptain && (isSuperAdmin(currentUser) || isRegistrationManager(currentUser)),
                                  disabled: assigningCaptainId === member.teamMemberId,
                                  onClick: () => {
                                    if (!assigningCaptainId) handleAssignCaptain(member.teamMemberId);
                                  },
                                },
                                {
                                  label: "View Profile",
                                  icon: <Eye size={16} />,
                                  onClick: () => {
                                    const currentUrl = location.pathname + location.search;
                                    navigate(
                                      `/dashboard/players/${member.playerId}?goTo=team&playerId=${
                                        member.playerId
                                      }&returnUrl=${encodeURIComponent(currentUrl)}`
                                    );
                                  },
                                },
                                {
                                  label: "Whatsapp",
                                  icon: <Telephone size={16} />,
                                  visible: !!member.phoneNumber,
                                  onClick: () => {
                                    window.open(`https://wa.me/${member.phoneNumber}`, "_blank");
                                  },
                                },
                                {
                                  label: "Telegram",
                                  icon: <BrandTelegram size={16} />,
                                  visible: !!member.phoneNumber,
                                  onClick: () => {
                                    window.open(`https://t.me/${member.phoneNumber}`, "_blank");
                                  },
                                },
                                {
                                  label: "Email",
                                  icon: <Envelope size={16} />,
                                  onClick: () => {
                                    window.location.href = `mailto:${member.email}`;
                                  },
                                },
                                {
                                  label: "Add to Contacts",
                                  icon: <Users size={16} />,
                                  onClick: () => {
                                    addMemberToContacts(member);
                                  },
                                },
                                {
                                  label: "Delete",
                                  icon: <Trash size={16} />,
                                  tone: "danger",
                                  onClick: () => {
                                    handleDeleteTeamMember(member.id);
                                  },
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableWrap>
            </div>
          )}

          {activeTab === "matches" && (
            <div className="team-detail__matches">
              {matchesLoading ? (
                <Loader />
              ) : matches.length === 0 ? (
                <Card padding="none">
                  <EmptyState icon={<Trophy />} title="No matches found for this team" />
                </Card>
              ) : (
                <>
                  {(() => {
                    // Group matches by format
                    const matchesByFormat = matches.reduce((acc, match) => {
                      const format = match.formatName || match.formatType;
                      if (!acc[format]) {
                        acc[format] = [];
                      }
                      acc[format].push(match);
                      return acc;
                    }, {} as Record<string, typeof matches>);

                    return Object.entries(matchesByFormat).map(([format, formatMatches]) => {
                      // Format display names
                      const getFormatDisplayName = (formatType: string) => {
                        switch (formatType.toLowerCase()) {
                          case "league":
                            return "League Phase";
                          case "knockout":
                            return "Knockout Phase";
                          case "group":
                            return "Group Stage";
                          default:
                            return formatType;
                        }
                      };

                      return (
                        <Card
                          key={format}
                          title={getFormatDisplayName(format)}
                          actions={
                            <Tag tone="neutral" size="md">
                              {formatMatches.length} match{formatMatches.length !== 1 ? "es" : ""}
                            </Tag>
                          }
                        >
                          <div className="team-detail__match-grid">
                            {formatMatches.map((match) => {
                              // Convert ITeamMatch to IMatchDetailDTO format for MatchCard
                              const matchDetailDTO: IMatchDetailDTO = {
                                id: match.id,
                                homeTeamId: match.homeTeamId,
                                homeTeamName: match.homeTeamName,
                                homeTeam2Name: match.homeTeam2Name,
                                homeExtraUnits: match.homeExtraUnits,
                                awayTeamId: match.awayTeamId,
                                awayTeamName: match.awayTeamName,
                                awayTeam2Name: match.awayTeam2Name,
                                awayExtraUnits: match.awayExtraUnits,
                                round: match.round,
                                bracket: match.formatType, // Use formatType as bracket
                                venue: match.venue,
                                startTime: match.startTime,
                                winnerTeamId: match.winnerTeamId,
                                homeTeamSets: match.homeTeamSets,
                                awayTeamSets: match.awayTeamSets,
                                bestOf: match.bestOf,
                                gameScores: match.gameScores,
                                isCompleted: match.isCompleted,
                                group: match.formatType, // Use formatType as group for display
                              };

                              return <MatchCard key={match.id} match={matchDetailDTO} />;
                            })}
                          </div>
                        </Card>
                      );
                    });
                  })()}
                </>
              )}
            </div>
          )}
        </>
      )}
      {activePlayer ? (
        <PlayerStatusDialog
          item={activePlayer}
          teamId={teamDetail?.id!}
          isOpen={!!activePlayer}
          onConfirm={() => {
            setActivePlayer(undefined);
            const params = new URLSearchParams(location.search);
            params.delete("memberId");
            navigate({ search: params.toString() }, { replace: true });

            setLoading(true);
            getTournamentData();
          }}
          onClose={() => {
            setActivePlayer(undefined);
            const params = new URLSearchParams(location.search);
            params.delete("memberId");
            navigate({ search: params.toString() }, { replace: true });
          }}
        />
      ) : null}
      {showAddMemberDialog && teamDetail && (
        <AddTeamMemberDialog
          teamId={teamDetail.id}
          currentMembers={teamDetail.teamMembers}
          maxPlayers={maxPlayers}
          isOpen={showAddMemberDialog}
          onConfirm={() => {
            setShowAddMemberDialog(false);
            setLoading(true);
            getTournamentData();
            dispatch(setGeneralAlertMessage({ message: "Team members added successfully", type: "success" }));
          }}
          onClose={() => {
            setShowAddMemberDialog(false);
          }}
        />
      )}
    </div>
  );
};

export default TeamDetail;
